import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon, FilterIcon, XIcon, HomeIcon, MapIcon } from 'lucide-react';
import ListingCard from '../components/ListingCard';
import { mockListings, Listing } from '../utils/mockData';
const BrowseListingsPage = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'all';
  const [listings, setListings] = useState<Listing[]>([]);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: initialType,
    listingType: 'all',
    minPrice: '',
    maxPrice: '',
    minBedrooms: '',
    search: ''
  });
  useEffect(() => {
    // Only show approved listings to public
    const approvedListings = mockListings.filter(listing => listing.status === 'approved');
    setListings(approvedListings);
  }, []);
  useEffect(() => {
    let results = [...listings];
    if (filters.type !== 'all') {
      results = results.filter(listing => listing.type === filters.type);
    }
    if (filters.listingType !== 'all') {
      results = results.filter(listing => listing.listingType === filters.listingType);
    }
    if (filters.minPrice) {
      results = results.filter(listing => listing.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      results = results.filter(listing => listing.price <= parseInt(filters.maxPrice));
    }
    if (filters.minBedrooms && filters.type !== 'land') {
      results = results.filter(listing => listing.type === 'house' && listing.bedrooms && listing.bedrooms >= parseInt(filters.minBedrooms));
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(listing => listing.title.toLowerCase().includes(searchLower) || listing.description.toLowerCase().includes(searchLower) || listing.location.toLowerCase().includes(searchLower));
    }
    setFilteredListings(results);
  }, [listings, filters]);
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };
  const clearFilters = () => {
    setFilters({
      type: 'all',
      listingType: 'all',
      minPrice: '',
      maxPrice: '',
      minBedrooms: '',
      search: ''
    });
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-557">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-558">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-559">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0" data-id="element-560">
            Browse Properties
          </h1>
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="flex items-center bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-561">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-562" />
            Filters
          </button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearchSubmit} className="mb-6" data-id="element-563">
          <div className="relative" data-id="element-564">
            <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search by location, property name or description..." className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white shadow-sm" data-id="element-565" />
            <SearchIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" data-id="element-566" />
          </div>
        </form>

        {/* Filters */}
        {isFilterOpen && <div className="bg-white rounded-xl shadow-md p-6 mb-6" data-id="element-567">
            <div className="flex justify-between items-center mb-4" data-id="element-568">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-569">Filters</h2>
              <button onClick={clearFilters} className="text-sm text-primary hover:text-[rgb(var(--color-primary-dark))] flex items-center font-medium" data-id="element-570">
                <XIcon className="h-4 w-4 mr-1" data-id="element-571" />
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-572">
              <div data-id="element-573">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-574">
                  Property Type
                </label>
                <select name="type" value={filters.type} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-575">
                  <option value="all" data-id="element-576">All Properties</option>
                  <option value="house" data-id="element-577">Houses</option>
                  <option value="land" data-id="element-578">Land</option>
                </select>
              </div>
              <div data-id="element-579">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-580">
                  Listing Type
                </label>
                <select name="listingType" value={filters.listingType} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-581">
                  <option value="all" data-id="element-582">All</option>
                  <option value="sale" data-id="element-583">For Sale</option>
                  <option value="rent" data-id="element-584">For Rent</option>
                </select>
              </div>
              <div data-id="element-585">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-586">
                  Min Price
                </label>
                <input type="number" name="minPrice" value={filters.minPrice} onChange={handleFilterChange} placeholder="Min Price" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-587" />
              </div>
              <div data-id="element-588">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-589">
                  Max Price
                </label>
                <input type="number" name="maxPrice" value={filters.maxPrice} onChange={handleFilterChange} placeholder="Max Price" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-590" />
              </div>
              {filters.type !== 'land' && <div data-id="element-591">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-592">
                    Min Bedrooms
                  </label>
                  <select name="minBedrooms" value={filters.minBedrooms} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-593">
                    <option value="" data-id="element-594">Any</option>
                    <option value="1" data-id="element-595">1+</option>
                    <option value="2" data-id="element-596">2+</option>
                    <option value="3" data-id="element-597">3+</option>
                    <option value="4" data-id="element-598">4+</option>
                    <option value="5" data-id="element-599">5+</option>
                  </select>
                </div>}
            </div>
          </div>}

        {/* Quick Filters */}
        <div className="flex items-center justify-between mb-6" data-id="element-600">
          <div className="text-gray-600" data-id="element-601">
            <span className="font-medium text-gray-900" data-id="element-602">
              {filteredListings.length}
            </span>{' '}
            properties found
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-2" data-id="element-603">
            <button onClick={() => setFilters(prev => ({
            ...prev,
            type: prev.type === 'house' ? 'all' : 'house'
          }))} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all ${filters.type === 'house' ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-primary'}`} data-id="element-604">
              <HomeIcon className="h-4 w-4 mr-1.5" data-id="element-605" />
              Houses
            </button>
            <button onClick={() => setFilters(prev => ({
            ...prev,
            type: prev.type === 'land' ? 'all' : 'land'
          }))} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all ${filters.type === 'land' ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-primary'}`} data-id="element-606">
              <MapIcon className="h-4 w-4 mr-1.5" data-id="element-607" />
              Land
            </button>
            <button onClick={() => setFilters(prev => ({
            ...prev,
            listingType: prev.listingType === 'sale' ? 'all' : 'sale'
          }))} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filters.listingType === 'sale' ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-primary'}`} data-id="element-608">
              For Sale
            </button>
            <button onClick={() => setFilters(prev => ({
            ...prev,
            listingType: prev.listingType === 'rent' ? 'all' : 'rent'
          }))} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filters.listingType === 'rent' ? 'bg-green-600 text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-green-600'}`} data-id="element-609">
              For Rent
            </button>
          </div>
        </div>

        {/* Listings Grid */}
        {filteredListings.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-610">
            {filteredListings.map(listing => <ListingCard key={listing.id} listing={listing} data-id="element-611" />)}
          </div> : <div className="bg-white rounded-xl shadow-md p-12 text-center" data-id="element-612">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-613">
              <SearchIcon className="h-8 w-8 text-gray-400" data-id="element-614" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-615">
              No properties found
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-616">
              Try adjusting your filters or search criteria
            </p>
            <button onClick={clearFilters} className="btn-primary" data-id="element-617">
              Clear Filters
            </button>
          </div>}
      </div>
    </div>;
};
export default BrowseListingsPage;